import {observer} from 'mobx-react-lite';
import React, {useEffect, useState} from 'react';
import {
  Autocomplete,
  AutocompleteRenderInputParams,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import {Form, Formik, FormikProps} from 'formik';
import Grid from '@mui/material/Grid2';
import Box from '@mui/material/Box';
import {DropdownOption} from '../../../../entity/core/dropdown-option';
import PlacesAutocomplete from 'react-google-places-autocomplete';
import {ParcelDialogProps} from '../../../../entity/props/project/parcel-dialog-props';
import {ManageMode} from '../../../../entity/enums/manage-mode';
import {useService} from '../../../../infrastructure/service/service';
import {ParcelInfoDto} from '../../../../entity/dto/parcel-info-dto';
import {State} from '../../../../entity/model/state';
import {County} from '../../../../entity/model/county';
import {Organization} from '../../../../entity/model/organization';
import {User} from '../../../../entity/model/user';
import {TitleSectionTemplate} from '../../../../entity/model/title-section-template';

import '../../../../assets/css/style.css';

const ParcelDialog = (props: ParcelDialogProps): JSX.Element => {
  const {projectService, organizationService, userService, titleSectionTemplateService} = useService();

  const [currentUser, setCurrentUser] = useState<User>();
  const [sameOrganization, setSameOrganization] = useState<boolean>(false);
  const [stateOptions, setStateOptions] = useState<DropdownOption[]>([]);
  const [countyOptions, setCountyOptions] = useState<DropdownOption[]>([]);
  const [organizationOptions, setOrganizationOptions] = useState<DropdownOption[]>([]);
  const [titleSectionTemplateOptions, setTitleSectionTemplateOptions] = useState<DropdownOption[]>([]);
  const [originalParcelValues, setOriginalParcelValues] = useState<ParcelInfoDto>();
  const [isButtonDisabled, setButtonDisabled] = useState<boolean>(true);

  useEffect(() => {
    const init = async () => {
      const user = await userService.getCurrentUser();
      setCurrentUser(user);

      setStateOptions(props.states.map((state: State) => ({key: state.id, text: state.shortName, value: state.id})));

      const organizations = await organizationService.getOrganizations(user?.organizationID);
      setOrganizationOptions(organizations.map((organization: Organization) => ({key: organization.id, text: organization.name, value: organization.id})));

      if (props.mode === ManageMode.CREATE) {
        let stateCounties = props.counties.filter((county: County) => county.stateID === props.defaultData?.stateID).sort((a: County, b: County) => a.name.localeCompare(b.name));
        setCountyOptions(stateCounties.map((county: County) => ({key: county.id, text: county.name, value: county.id})));

        setSameOrganization(user?.organizationID === props.defaultData?.assignedToOrganizationID);
      } else {
        let stateCounties = props.counties.filter((county: County) => county.stateID === props.parcelEntry?.stateID).sort((a: County, b: County) => a.name.localeCompare(b.name));
        setCountyOptions(stateCounties.map((county: County) => ({key: county.id, text: county.name, value: county.id})));

        setSameOrganization(user?.organizationID === props.parcelEntry?.assignedToOrganizationID);
      }

      const titleSectionTemplates = await titleSectionTemplateService.getTitleSectionTemplates(user?.organizationID ?? '', false);

      let options: DropdownOption[] = titleSectionTemplates.map((titleSectionTemplate: TitleSectionTemplate) => ({key: titleSectionTemplate.id, text: titleSectionTemplate.name, value: titleSectionTemplate.id}));
      options.push({key: 'none', text: 'None', value: ''});

      setTitleSectionTemplateOptions(options);

      if (props.mode === ManageMode.UPDATE && props.parcelEntry)
        setOriginalParcelValues(props.parcelEntry);
    }

    init();
  }, []);


  const handleValidate = (values: ParcelInfoDto): Record<string, string> => {
    const errors: Record<string, string> = {};

    if (!values.assignedToApproverUserID)
      errors.assignedToApproverUserID = 'Final Approver is required.';

    if (!values.assignedToUserID && sameOrganization)
      errors.assignedToUserID = 'Abstractor is required.';

    if (!values.assignedToReviewerUserID && sameOrganization)
      errors.assignedToReviewerUserID = 'Reviewer is required.';

    if (!values.stateID)
      errors.stateID = 'State is required.';

    if (!values.countyID)
      errors.countyID = 'County is required.';

    if (!values.tmsParcelNumber)
      errors.tmsParcelNumber = 'TMS Parcel Number is required.';

    if (values.tmsParcelNumber && props.existingParcelEntries) {
      const isDuplicate = props.existingParcelEntries.some((entry: ParcelInfoDto) => 
        entry.tmsParcelNumber === values.tmsParcelNumber && entry.id !== values.id
      );
      
      if (isDuplicate)
        errors.tmsParcelNumber = 'This TMS/Parcel Number already exists.  ';
    }

    if (!values.address)
      errors.address = 'Address is required.';

    if (!values.assignedToOrganizationID)
      errors.assignedToOrganizationID = 'Abstractor Organization is required.';

    if (props.mode === ManageMode.UPDATE) {
      if (!originalParcelValues)
        setButtonDisabled(true);
      else {
        const hasChanges = 
          values.tmsParcelNumber !== originalParcelValues.tmsParcelNumber ||
          values.address !== originalParcelValues.address ||
          values.stateID !== originalParcelValues.stateID ||
          values.countyID !== originalParcelValues.countyID ||
          values.assignedToOrganizationID !== originalParcelValues.assignedToOrganizationID ||
          values.assignedToUserID !== originalParcelValues.assignedToUserID ||
          values.assignedToReviewerUserID !== originalParcelValues.assignedToReviewerUserID ||
          values.assignedToApproverUserID !== originalParcelValues.assignedToApproverUserID;

        setButtonDisabled(!hasChanges);
      }
    }

    if (props.mode === ManageMode.CREATE) {
      const hasAllRequiredFields = 
        values.stateID &&
        values.countyID &&
        values.address &&
        values.tmsParcelNumber &&
        values.assignedToOrganizationID &&
        (!sameOrganization || (values.assignedToUserID && values.assignedToReviewerUserID)) &&
        (props.createdOrganization ? values.assignedToApproverUserID : true);
      
      setButtonDisabled(!hasAllRequiredFields);
    }

    return errors;
  }

  const handleStateChange = (handleChange: any) => (event: any) => {
    let stateCounties = props.counties.filter((county: County) => county.stateID === event.target.value).sort((a: County, b: County) => a.name.localeCompare(b.name));
    setCountyOptions(stateCounties.map((county: County) => ({key: county.id, text: county.name, value: county.id})));

    handleChange({target: {name: event.target.name, value: event.target.value}});
  }

  const handleOrganizationChange = (event: React.SyntheticEvent<Element, Event>, newValue: DropdownOption | null, handleChange: any) => {
    handleChange({ target: { name: 'assignedToOrganizationID', value: newValue?.value || '' }});
    setSameOrganization(newValue?.value === currentUser?.organizationID);
  }

  return (
    <Dialog open={props.open} className={'zitles-dialog large'}>
      <DialogTitle className={'zitles-dialog-title'}>
        <span>{`${props.mode === ManageMode.CREATE ? 'Add' : 'Edit'} Parcel Entry`}</span>
        <IconButton id={'btn-parcel-dialog-close'} onClick={(): void => props.setOpen(false)}>
          <CloseIcon className={'dialog-icon'}/>
        </IconButton>
      </DialogTitle>
      <DialogContent dividers className={'zitles-dialog-content large'}>
        <Formik
          initialValues={projectService.getParcelInfoDto(props.mode, props.parcelEntry, props.defaultData)}
          validate={handleValidate}
          onSubmit={props.handleSubmit}>
          {({values, errors, touched, handleChange, handleBlur, handleSubmit, setFieldValue}: FormikProps<ParcelInfoDto>): JSX.Element => (
            <Form onSubmit={handleSubmit}>
              <Grid container spacing={2}>
                <Grid size={3}>
                  <FormControl required fullWidth error={touched.stateID && Boolean(errors.stateID)}>
                    <InputLabel>State</InputLabel>
                    <Select
                      id={'input-state'}
                      name='stateID'
                      value={values.stateID}
                      label='State'
                      onBlur={handleBlur}
                      onChange={handleStateChange(handleChange)}>
                      {
                        stateOptions.map((option: DropdownOption): JSX.Element => <MenuItem key={option.key} value={option.value}>{option.text}</MenuItem>)
                      }
                    </Select>
                    <FormHelperText>{touched.stateID && errors.stateID}</FormHelperText>
                  </FormControl>
                </Grid>
                <Grid size={3}>
                  <FormControl required fullWidth error={touched.countyID && Boolean(errors.countyID)}>
                    <InputLabel>County</InputLabel>
                    <Select
                      id={'input-county'}
                      name='countyID'
                      value={values.countyID}
                      label='County'
                      onBlur={handleBlur}
                      onChange={(event: SelectChangeEvent) => handleChange({target: {name: event.target.name, value: event.target.value}})}>
                      {
                        countyOptions.map((option: DropdownOption): JSX.Element => <MenuItem key={option.key} value={option.value}>{option.text}</MenuItem>)
                      }
                    </Select>
                    <FormHelperText>{touched.countyID && errors.countyID}</FormHelperText>
                  </FormControl>
                </Grid>
                <Grid size={3}>
                  <PlacesAutocomplete
                    apiKey={process.env.REACT_APP_GOOGLE_API_KEY}
                    selectProps={{
                      inputId: 'input-address',
                      value: values.address ? { label: values.address, value: {description: values.address} } : null,
                      onChange: (selected) => setFieldValue('address', selected?.value.description || ''),
                      onInputChange: (value: string): void => {
                        if (value !== '')
                          setFieldValue('address', value)
                      },
                      placeholder: ' Address'
                    }}/>
                  {
                    touched.address && errors.address &&
                      <Typography className='zitles-form-error'>{errors.address}</Typography>
                  }
                </Grid>
                <Grid size={3}>
                  <TextField
                    id={'input-tms'}
                    required
                    fullWidth
                    label='TMS/Parcel Number'
                    name='tmsParcelNumber'
                    value={values.tmsParcelNumber}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.tmsParcelNumber && Boolean(errors.tmsParcelNumber)}
                    helperText={touched.tmsParcelNumber && errors.tmsParcelNumber}/>
                </Grid>
                {
                  props.mode === ManageMode.CREATE &&
                    <Grid size={6}>
                      <FormControl fullWidth>
                        <InputLabel sx={{zIndex: 0}}>Title Section Template</InputLabel>
                        <Select
                          id={'input-title-section-template'}
                          name="titleSectionTemplateID"
                          value={values.titleSectionTemplateID}
                          label="Title Section Template"
                          onChange={(event: SelectChangeEvent) => handleChange({target: {name: event.target.name, value: event.target.value}})}>
                          {
                            titleSectionTemplateOptions.map((option: DropdownOption): JSX.Element => <MenuItem key={option.key} value={option.value}>{option.text}</MenuItem>)
                          }
                        </Select>
                      </FormControl>
                    </Grid>
                }
                <Grid size={props.mode === ManageMode.CREATE ? 6 : 12}>
                  <Autocomplete
                    options={organizationOptions}
                    getOptionLabel={(option: DropdownOption): string => option.text}
                    value={organizationOptions.find((option: DropdownOption): boolean => option.value === values.assignedToOrganizationID) || null}
                    onChange={(event: React.SyntheticEvent<Element, Event>, newValue: DropdownOption | null): void => handleOrganizationChange(event, newValue, handleChange)}
                    onBlur={handleBlur}
                    renderInput={(params: AutocompleteRenderInputParams): JSX.Element => (
                      <TextField
                        {...params}
                        id={'input-assign-organization'}
                        name='assignedToOrganizationID'
                        label='Title Abstractor Organization'
                        sx={{ zIndex: 0 }}
                        error={touched.assignedToOrganizationID && Boolean(errors.assignedToOrganizationID)}
                        helperText={touched.assignedToOrganizationID && errors.assignedToOrganizationID}
                        required
                        fullWidth/>
                    )}/>
                </Grid>
                {
                  sameOrganization &&
                    <>
                      <Grid size={props.createdOrganization ? 4 : 6}>
                        <FormControl required fullWidth error={touched.assignedToUserID && Boolean(errors.assignedToUserID)}>
                          <InputLabel sx={{zIndex: 0}}>Abstractor</InputLabel>
                          <Select
                            id={'input-abstractor'}
                            name="assignedToUserID"
                            value={values.assignedToUserID}
                            label="Abstractor"
                            onBlur={handleBlur}
                            onChange={(event: SelectChangeEvent) => handleChange({target: {name: event.target.name, value: event.target.value}})}>
                            {props.usersOptions.map((option: DropdownOption): JSX.Element => <MenuItem key={option.key} value={option.value}>{option.text}</MenuItem>)}
                          </Select>
                          <FormHelperText>{touched.assignedToUserID && errors.assignedToUserID}</FormHelperText>
                        </FormControl>
                      </Grid>
                      <Grid size={props.createdOrganization ? 4 : 6}>
                        <FormControl required fullWidth error={touched.assignedToReviewerUserID && Boolean(errors.assignedToReviewerUserID)}>
                          <InputLabel sx={{zIndex: 0}}>Reviewer</InputLabel>
                          <Select
                            id={'input-reviewer'}
                            name="assignedToReviewerUserID"
                            value={values.assignedToReviewerUserID}
                            label="Reviewer"
                            onBlur={handleBlur}
                            onChange={(event: SelectChangeEvent) => handleChange({target: {name: event.target.name, value: event.target.value}})}>
                            {props.usersOptions.map((option: DropdownOption): JSX.Element => <MenuItem key={option.key} value={option.value}>{option.text}</MenuItem>)}
                          </Select>
                          <FormHelperText>{touched.assignedToReviewerUserID && errors.assignedToReviewerUserID}</FormHelperText>
                        </FormControl>
                      </Grid>
                    </>
                }
                {
                  props.createdOrganization &&
                    <Grid size={sameOrganization ? 4 : 12}>
                      <FormControl required fullWidth error={touched.assignedToApproverUserID && Boolean(errors.assignedToApproverUserID)}>
                        <InputLabel sx={{zIndex: 0}}>Approver</InputLabel>
                        <Select
                          id={'input-approver'}
                          name="assignedToApproverUserID"
                          value={values.assignedToApproverUserID}
                          label="Final Approver"
                          onBlur={handleBlur}
                          onChange={(event: SelectChangeEvent) => handleChange({target: {name: event.target.name, value: event.target.value}})}>
                          {props.usersOptions.map((option: DropdownOption): JSX.Element => <MenuItem key={option.key} value={option.value}>{option.text}</MenuItem>)}
                        </Select>
                        <FormHelperText>{touched.assignedToApproverUserID && errors.assignedToApproverUserID}</FormHelperText>
                      </FormControl>
                    </Grid>
                }
              </Grid>
              <Divider sx={{ my: 2, mx: -3 }} />
              <Box className={'action-btn-container'}>
                <Button
                  id={'btn-parcel-cancel'}
                  disableTouchRipple
                  onClick={(): void => props.setOpen(false)}
                  className={'zitles-button cancel'}>
                  Cancel
                </Button>
                <Button
                  id={'btn-parcel-submit'}
                  type='submit'
                  disabled={isButtonDisabled || Object.keys(errors).length > 0}
                  className={`zitles-button submit ${isButtonDisabled || Object.keys(errors).length > 0 ? 'disable' : ''}`}>
                  Submit
                </Button>
              </Box>
            </Form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
}

export default observer(ParcelDialog);
